/**
 * Feature E05-F01 — "Admin: пользователи" request/response contracts.
 *
 * 1:1 port of the legacy n8n `manage_users` action router (group Users,
 * docs/source-n8n/reference/Users.md + sql-queries.md "Users: Get All / Execute
 * Create / Execute Update / Execute Delete"). The old SPA POSTed
 * `{ command:'manage_users', action, ...payload, session_token }` to one webhook;
 * the v2 transport is clean REST under `/admin/users`, but the SAME logical
 * contract — CRUD over `users` + the IMAP/OAuth `mailbox_accounts` rows.
 *
 * Endpoints honoured by the feature route (`routes/admin-users.ts`):
 *   GET    /admin/users          → { success:true, users: AdminUserDTO[] }   (get_users)
 *   GET    /admin/users/:id/email-signature
 *                                → { success:true, id, email_signature }     (E10-F04-T10)
 *   POST   /admin/users          → { success:true, id }                      (create_user)
 *   PATCH  /admin/users/:id      → { success:true, id }                      (update_user)
 *   DELETE /admin/users/:id      → { success:true, message:'User deleted' }  (delete_user)
 *   POST   /admin/users/:id/disconnect-outlook
 *                                → { success:true, message:'Outlook disconnected' } (disconnect_outlook)
 *
 * Parity notes carried over from the legacy slice (Users.md gotchas), and the
 * security hardening the v2 port adds on top:
 *   - get_users: NO `password` field on any user (legacy leaked imap/smtp
 *     passwords + plaintext; v2 strips ALL secrets — T01 acceptance #1 and T02
 *     acceptance #3). ORDER BY created_at ASC (Users: Get All).
 *   - create_user: `id` is derived from the email local-part (text before `@`,
 *     lowercase, every `[^a-z0-9]` → `_`); `avatar` defaults to the name initials
 *     (≤2 chars, upper) when blank; a duplicate email is a silent no-op (legacy
 *     `ON CONFLICT (email) DO NOTHING`) — NOT an error (T01 acceptance #2, #3).
 *   - password: argon2-hashed (legacy stored plaintext — T02). On update an empty
 *     password leaves the stored hash untouched (T02 acceptance #2).
 *   - mailbox: when `mailbox.imap_host` is filled the IMAP `mailbox_accounts` row
 *     is created/updated (auth_type='imap'); when blank the row is left alone
 *     (T04). The email-cascade keeps imap_user/smtp_user synced with users.email.
 *
 * IDOR hardening: the legacy slice trusted `body.userId` and never checked the
 * caller's role/session — any user could edit/delete/escalate any user. v2
 * mounts the route behind `requireRole('admin')` and reads the target id ONLY
 * from the path param. No userId is ever read from the body.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Roles (Users.md: recruiter | teamlead | admin)
// ---------------------------------------------------------------------------
/** The known user roles (mirrors APP_ROLES; default for a new user is recruiter). */
export const ADMIN_USER_ROLES = ['recruiter', 'teamlead', 'admin'];
/** Default role for a freshly created user (legacy `role='recruiter'`). */
export const ADMIN_USER_DEFAULT_ROLE = 'recruiter';
/**
 * RU/EN labels + badge colours for the role pill (T03 acceptance #3:
 * admin = purple, teamlead = blue, recruiter = gray). The web view renders these
 * tailwind classes verbatim (1:1 with legacy dashboard.js `roleBadge`).
 */
export const ADMIN_USER_ROLE_LABELS = {
    recruiter: 'Recruiter',
    teamlead: 'Team Lead',
    admin: 'Admin',
};
export const ADMIN_USER_ROLE_BADGE_CLASS = {
    admin: 'bg-purple-100 text-purple-700',
    teamlead: 'bg-blue-100 text-blue-700',
    recruiter: 'bg-gray-100 text-gray-700',
};
/** Permissive role schema (column is varchar(50), legacy never enforced an enum). */
export const adminUserRoleSchema = z.string().min(1).max(50);
// ---------------------------------------------------------------------------
// Mailbox (IMAP/SMTP) — the `mailbox` json_build_object of Users: Get All
// ---------------------------------------------------------------------------
/**
 * The IMAP/SMTP mailbox settings as returned to the admin UI. Mirrors the legacy
 * `json_build_object(...)` projection of the IMAP `mailbox_accounts` row, with
 * one HARDENING difference: the legacy query leaked `imap_password`/`smtp_password`
 * to the client in plaintext (Users.md gotcha). v2 NEVER returns the passwords —
 * only the connection metadata, plus boolean `has_imap_password` / `has_smtp_password`
 * flags so the dialog can show "password set" without exposing the secret.
 *
 * `null` host/user fields mean "no IMAP account configured" (legacy still built
 * the object from NULLs when no imap row existed; v2 returns `mailbox:null` in
 * that case, see `adminUserSchema.mailbox`).
 */
export const adminUserMailboxSchema = z.object({
    imap_host: z.string().nullable(),
    imap_port: z.number().int().nullable(),
    imap_user: z.string().nullable(),
    smtp_host: z.string().nullable(),
    smtp_port: z.number().int().nullable(),
    smtp_user: z.string().nullable(),
    /** Secrets are never sent — only whether one is stored (T02 acceptance #3). */
    has_imap_password: z.boolean(),
    has_smtp_password: z.boolean(),
});
// ---------------------------------------------------------------------------
// Read DTO — Users: Get All projection (password-free)
// ---------------------------------------------------------------------------
/**
 * One user as listed in the admin Users table. Mirrors the legacy `Users: Get
 * All` projection MINUS every secret: no `users.password`, no imap/smtp
 * passwords (T01 acceptance #1, T02 acceptance #3). `mailbox` is `null` when the
 * user has no active IMAP account (rather than an all-NULL object).
 */
export const adminUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: adminUserRoleSchema,
    team_id: z.string().nullable(),
    avatar: z.string().nullable(),
    /** ISO 8601 timestamp. */
    created_at: z.string(),
    /** Active Microsoft OAuth account present (auth_type='oauth', is_active=true). */
    has_outlook: z.boolean(),
    outlook_email: z.string().nullable(),
    /** IMAP/SMTP settings (password-free) or null when no active imap account. */
    mailbox: adminUserMailboxSchema.nullable(),
});
/**
 * GET /admin/users/:id/email-signature response — the stored `users.email_signature`
 * HTML of ONE user (`''` when the column is NULL/empty).
 *
 * Why the signature is fetched per user instead of riding on {@link adminUserSchema}:
 * a signature carries its logo as a `data:` URI (see `@eer/shared/inlineImages` for
 * why that carrier was chosen) and may reach ~256 KB of base64 EACH
 * (`INLINE_IMAGE_MAX_BASE64_BYTES`). Putting it in the list projection would multiply
 * that by the number of users on EVERY render of the admin Users tab — megabytes of
 * response for a field only the open Edit dialog can show. So the list stays lean and
 * the dialog pulls the one signature it is about to display (E10-F04-T10).
 *
 * The value is returned VERBATIM — deliberately NOT run through `sanitizeRichText`
 * on the way out: the sanitizer's scheme allow-list excludes `data:`, so sanitizing
 * the read path would silently strip the logo the admin just saved (the outbound
 * email path handles this properly via `prepareRichTextForEmail`, which converts the
 * `data:` image into a `cid:` attachment BEFORE sanitizing).
 */
export const adminUserSignatureResponseSchema = z.object({
    // Optional: the web apiClient strips `success` from object bodies via
    // unwrapLegacySuccess before the query parses; the backend still SENDS it.
    success: z.literal(true).optional(),
    id: z.string(),
    /** Stored signature HTML; `''` when the user has none. */
    email_signature: z.string(),
});
/** GET /admin/users response — all users, ORDER BY created_at ASC. */
export const adminUsersListResponseSchema = z.object({
    // Optional: the web apiClient strips `success` from object bodies via
    // unwrapLegacySuccess before the query parses; the backend still SENDS it.
    // `.optional()` accepts both the raw `{success:true,...}` and the unwrapped form.
    success: z.literal(true).optional(),
    users: z.array(adminUserSchema),
});
// ---------------------------------------------------------------------------
// Mailbox input — body.mailbox.* of create_user / update_user
// ---------------------------------------------------------------------------
/**
 * The mailbox settings the admin form submits. Mirrors the legacy `body.mailbox`
 * (Users: Build Create/Update SQL). Only meaningful when `imap_host` is filled —
 * a blank `imap_host` means "do not touch the imap row" (T04 acceptance #2). Port
 * defaults match the legacy COALESCE: imap_port||993, smtp_port||465; smtp_host
 * defaults to imap_host, smtp_user to imap_user, smtp_password to imap_password.
 */
export const adminUserMailboxInputSchema = z.object({
    imap_host: z.string().trim().optional(),
    imap_port: z.coerce.number().int().optional(),
    imap_user: z.string().trim().optional(),
    imap_password: z.string().optional(),
    smtp_host: z.string().trim().optional(),
    smtp_port: z.coerce.number().int().optional(),
    smtp_user: z.string().trim().optional(),
    smtp_password: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Create — Users: Build/Execute Create
// ---------------------------------------------------------------------------
/**
 * POST /admin/users body. `name` + `email` REQUIRED (legacy "Name and email
 * required"). `password` optional (legacy default 'password'; v2 still allows the
 * default but the UI requires it for a new user). `role` defaults to 'recruiter'.
 * `avatar` optional (derived from initials when blank). `mailbox` optional.
 */
export const createAdminUserSchema = z.object({
    name: z.string().trim().min(1, 'Name and email required'),
    email: z.string().trim().min(1, 'Name and email required'),
    password: z.string().optional(),
    role: adminUserRoleSchema.optional(),
    team_id: z.string().trim().nullable().optional(),
    avatar: z.string().trim().nullable().optional(),
    email_signature: z.string().optional(),
    mailbox: adminUserMailboxInputSchema.optional(),
});
// ---------------------------------------------------------------------------
// Update — Users: Build/Execute Update (partial)
// ---------------------------------------------------------------------------
/**
 * PATCH /admin/users/:id body. PARTIAL update — every field optional. An empty
 * `password` (or omitted) leaves the stored hash unchanged (T02 acceptance #2).
 * `team_id`/`avatar`: present + falsy → set NULL; absent → leave unchanged
 * (legacy `!==undefined` distinction). `mailbox` upserts the imap row when
 * `imap_host` is filled. At least one updatable field OR a mailbox must be
 * present (legacy "No fields to update").
 */
export const updateAdminUserSchema = z.object({
    name: z.string().trim().min(1).optional(),
    email: z.string().trim().min(1).optional(),
    /** Empty string / omitted → keep current hash; non-empty → re-hash. */
    password: z.string().optional(),
    role: adminUserRoleSchema.optional(),
    team_id: z.string().trim().nullable().optional(),
    avatar: z.string().trim().nullable().optional(),
    email_signature: z.string().optional(),
    mailbox: adminUserMailboxInputSchema.optional(),
});
// ---------------------------------------------------------------------------
// Mutation responses
// ---------------------------------------------------------------------------
/**
 * Success body for create/update — the affected user id (legacy RETURNING id).
 * On a duplicate-email create the legacy `ON CONFLICT DO NOTHING` produced no
 * row; v2 still answers `{ success:true }` (no error) and reports the existing id
 * so the no-op is observable (T01 acceptance #3).
 */
export const adminUserMutationResponseSchema = z.object({
    // Optional: the web apiClient strips `success` from object bodies via
    // unwrapLegacySuccess before the query parses; the backend still SENDS it.
    // `.optional()` accepts both the raw `{success:true,...}` and the unwrapped form.
    success: z.literal(true).optional(),
    id: z.string(),
});
/** Success body for delete — the static legacy message (always success). */
export const adminUserDeleteResponseSchema = z.object({
    // Optional: the web apiClient strips `success` from object bodies via
    // unwrapLegacySuccess before the query parses; the backend still SENDS it.
    // `.optional()` accepts both the raw `{success:true,...}` and the unwrapped form.
    success: z.literal(true).optional(),
    message: z.literal('User deleted'),
});
/** Success body for disconnect-outlook — the static legacy message. */
export const adminUserDisconnectOutlookResponseSchema = z.object({
    // Optional: the web apiClient strips `success` from object bodies via
    // unwrapLegacySuccess before the query parses; the backend still SENDS it.
    // `.optional()` accepts both the raw `{success:true,...}` and the unwrapped form.
    success: z.literal(true).optional(),
    message: z.literal('Outlook disconnected'),
});
/** The literal messages the legacy `Return Success` / `Return Disconnect OK` nodes sent. */
export const ADMIN_USER_DELETE_MESSAGE = 'User deleted';
export const ADMIN_USER_DISCONNECT_OUTLOOK_MESSAGE = 'Outlook disconnected';
//# sourceMappingURL=admin-users.js.map